/**
 * Physical storage vocabulary: how a logical field maps onto real PostgreSQL
 * columns.
 *
 * This is the contract between the field-type registry (which declares the
 * mapping) and the server's schema manager (which emits the DDL). It is
 * deliberately declarative: no SQL identifier is ever built here, because
 * identifier construction and quoting belong to the single `quoteIdent` on the
 * server (INV-2).
 *
 * A field owns one or MORE columns. Composite types (PHONE, CURRENCY) spread one
 * logical value across several columns; the suffix distinguishes them and the
 * role tells the record layer what each column means.
 */

export type PhysicalColumnRole =
  /** The column a filter/sort on this field targets. Exactly one per field. */
  | 'value'
  /** PHONE: the string the user actually typed, kept for display. */
  | 'original'
  /** CURRENCY: the integer amount in micros (1/1_000_000 of a unit). */
  | 'amountMicros'
  /** CURRENCY: ISO-4217 code of the amount beside it. */
  | 'currencyCode';

/**
 * `static` types are written into DDL verbatim. `fieldEnum` types are per-field
 * PostgreSQL enum types whose NAME derives from the field UUID (so it survives
 * renames) and is therefore resolved on the server, not here.
 */
export type PhysicalColumnType =
  | { readonly kind: 'static'; readonly sql: string }
  | { readonly kind: 'fieldEnum'; readonly array: boolean };

export interface PhysicalColumnSpec {
  /** Appended to the field's API name to form the column name. '' = the name itself. */
  readonly suffix: string;
  readonly type: PhysicalColumnType;
  readonly role: PhysicalColumnRole;
  /** Literal SQL default, already quoted for SQL (e.g. `'EUR'`). */
  readonly defaultSql?: string | undefined;
}

export const staticType = (sql: string): PhysicalColumnType => ({ kind: 'static', sql });

export const fieldEnumType = (array: boolean): PhysicalColumnType => ({ kind: 'fieldEnum', array });

/**
 * Every suffix any field type may append. Kept in one place so the identifier
 * validator can budget for the longest one (PostgreSQL truncates identifiers
 * over 63 bytes, which would silently collide two columns).
 */
export const COLUMN_SUFFIXES = {
  value: '',
  original: '_original',
  amountMicros: '_amount_micros',
  currencyCode: '_currency_code',
  relationId: '_id',
} as const;

export const MAX_COLUMN_SUFFIX_LENGTH = Math.max(
  ...Object.values(COLUMN_SUFFIXES).map((suffix) => suffix.length),
);

/** The single column a filter or sort on this field must target. */
export function valueColumn(
  columns: readonly PhysicalColumnSpec[],
): PhysicalColumnSpec | undefined {
  return columns.find((column) => column.role === 'value' || column.role === 'amountMicros');
}
